import { useMemo } from 'react'

import { matchFilterString, sortArrayBy } from '@domain-group/javascript-utils'

import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import { GeoStateModel } from '@models/types'

import type { EntityOptions, ModuleState } from '@redux/modules/types'

const watchEntityKeys = ['geoStates']

type GeoStatesFilters = {
  active?: string,
  country_code?: string,
  geo_country_id?: number,
}

interface UseGeoStatesOptions {
  entityKey?: string,
  filters?: GeoStatesFilters,
  performHttpRequests?: boolean,
  requestOptions?: EntityOptions,
}

function useGeoStates(options: UseGeoStatesOptions = {}) {
  const { filters = {} } = options
  const {
    active: filterActive,
    country_code: filterCountryCode,
    geo_country_id: filterGeoCountryId,
  } = filters

  const {
    updatedEntities: { geoStates: geoStatesUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const { geoCountries, geoStates } = useSelector(reduxState => reduxState.entities)

  // @ts-expect-error TS2538
  const geoCountry = geoCountries[filterGeoCountryId] || []
  const { code } = geoCountry

  const filteredGeoStates = useMemo((): GeoStateModel[] => {
    const filtered = Object.values(geoStates).filter((geoState) => {
      const { active, country_code } = geoState

      const matchActive = filterActive ? active?.toString() === filterActive : true
      const matchCountry = matchFilterString(country_code, filterCountryCode)
      const matchCountryId = matchFilterString(country_code, code)

      return matchActive && matchCountry && matchCountryId
    })

    return sortArrayBy(filtered, 'asc', 'title')
  }, [geoStatesUpdatedAt, JSON.stringify(filters)])

  const filteredGeoStatesCount = filteredGeoStates.length

  const loadMorePayload = useLoadMore({
    filters,
    loadedCount: filteredGeoStatesCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  const { loading: loadingGeoStates } = useReduxAction(
    'geoStates',
    'loadGeoStates',
    {
      ...filtersWithOffset,
      limit,
    },
    [performHttpRequests],
    {
      shouldPerformFn: ({ loading }: ModuleState) => performHttpRequests && !loading,
    },
  )

  return {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredGeoStates,
    filteredGeoStatesCount,
    loading: loadingGeoStates,
  }
}

export default useGeoStates
